import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import { IndianRupee, TrendingUp, Download, ChevronDown, Wallet, BadgeDollarSign, ArrowUpRight, Percent } from "lucide-react";
import StatCard from "../components/StatCard";
import AnimatedValue from "../components/AnimatedValue";
import { SectionHeader, PageHeader, SnowflakeRequired } from "../components/SectionHeader";
import { useFilters } from "../context/FilterContext";
import RAGBadge from "../components/RAGBadge";
import ThemeChip from "../components/ThemeChip";
import InfoTooltip from "../components/InfoTooltip";
import { appendFyToSearchParams, buildDashboardParams } from "../utils/dashboardFilters";
import { fmtCr, fmtCrNoSymbol, fmtDate, fmtNum, displayTheme } from "../utils/format";
import { getThemeDotColor, getStatusHexColor } from "../utils/dynamicColors";
import BudgetVsActualTable from "../components/BudgetVsActualTable";
import ThemeSanctionMatrix from "../components/ThemeSanctionMatrix";


export default function Financial() {
  const { filters, filterOptions, viewMode, setGlobalLoading, API } = useFilters();
  const [summary, setSummary] = useState(null);
  const [budgetDist, setBudgetDist] = useState({ data: [], categories: [] });
  const [budgetDistToggle, setBudgetDistToggle] = useState("theme");
  const [grantsTable, setGrantsTable] = useState({ data: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [tablePage, setTablePage] = useState(1);
  const [themeMetrics, setThemeMetrics] = useState([]);
  const [awpByTheme, setAwpByTheme] = useState({});
  const [themeFinView, setThemeFinView] = useState("chart");
  const [budgetDistView, setBudgetDistView] = useState("chart");
  const [exporting, setExporting] = useState(false);
  const sf = filterOptions.data_sources?.grants || filterOptions.data_available;

  const params = buildDashboardParams(filters, { view_mode: viewMode });

  const fetchAll = async () => {
    setLoading(true);
    setGlobalLoading(true);
    try {
      const [sumRes, budgetDistRes, tblRes, themeRes, awpRes] = await Promise.all([
        axios.get(`${API}/financial/summary`, { params }).catch(() => ({ data: {} })),
        axios.get(`${API}/financial/budget-distribution`, { params: { ...params, group_by: budgetDistToggle === "theme" ? "theme" : "trust" } }).catch(() => ({ data: { data: [], categories: [] } })),
        axios.get(`${API}/financial/grants-table`, { params: { ...params, page: tablePage, per_page: 20 } }).catch(() => ({ data: { data: [], total: 0 } })),
        axios.get(`${API}/thematic/comparison`, { params }).catch(() => ({ data: [] })),
        axios.get(`${API}/executive/trust-theme-awp`, { params: { fy: params.fy, theme: params.theme, trust: params.trust } }).catch(() => ({ data: { data: [], trusts: [] } })),
      ]);
      setSummary(sumRes.data);
      setBudgetDist(budgetDistRes.data);
      setGrantsTable(tblRes.data);
      // Aggregate AWP by theme
      const awpMap = {};
      (awpRes.data?.data || []).forEach(row => {
        const total = (awpRes.data?.trusts || []).reduce((s, t) => s + (row[`${t}_awp`] || 0), 0);
        if (row.theme) awpMap[row.theme] = total;
      });
      setAwpByTheme(awpMap);
      // Merge AWP-only themes (e.g., "New Initiative") into themeMetrics
      const grantThemes = new Set((themeRes.data || []).map(r => r.theme));
      const awpOnlyRows = Object.keys(awpMap)
        .filter(t => !grantThemes.has(t) && awpMap[t] > 0)
        .map(t => ({ theme: t, grants: 0, allocation_cr: 0, disbursed_cr: 0, disbursement_rate: null }));
      setThemeMetrics([...(themeRes.data || []), ...awpOnlyRows]);
    } catch (e) { console.error(e); }
    finally { setLoading(false); setGlobalLoading(false); }
  };

  useEffect(() => { fetchAll(); }, [filters.fy, filters.theme, filters.status, filters.trust, filters.grantee, viewMode, tablePage, budgetDistToggle]);

  // Export handler
  const handleExport = async () => {
    setExporting(true);
    try {
      const exportParams = new URLSearchParams();
      appendFyToSearchParams(exportParams, filters.fy);
      if (filters.theme) exportParams.append("theme", filters.theme);
      if (filters.trust) exportParams.append("trust", filters.trust);
      if (filters.status) exportParams.append("status", filters.status);
      const response = await axios.get(`${API}/financial/export?${exportParams.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `financial_summary_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="financial-page" className="space-y-6">
      <PageHeader title="Financial" badge="02" />

      {/* Hero Stats - hidden per user request */}
      {false && (() => {
        const totalAwp = Object.values(awpByTheme).reduce((s, v) => s + v, 0);
        const cards = [
          {
            title: "Total AWP", value: totalAwp > 0 ? fmtCr(totalAwp) : "—",
            icon: Wallet, gradient: "from-amber-50 to-orange-50", border: "border-amber-200",
            iconBg: "bg-amber-100", iconColor: "text-amber-600", valueColor: "text-amber-900",
          },
          {
            title: "Total Sanctioned", value: summary?.total_allocation_cr ? fmtCr(summary.total_allocation_cr) : "—",
            icon: BadgeDollarSign, gradient: "from-rose-50 to-red-50", border: "border-red-200",
            iconBg: "bg-red-100", iconColor: "text-red-600", valueColor: "text-red-900",
          },
          {
            title: "Total Disbursed", value: summary?.total_disbursement_cr ? fmtCr(summary.total_disbursement_cr) : "—",
            icon: ArrowUpRight, gradient: "from-emerald-50 to-green-50", border: "border-emerald-200",
            iconBg: "bg-emerald-100", iconColor: "text-emerald-600", valueColor: "text-emerald-900",
          },
          {
            title: "Disbursement Rate", value: summary?.disbursement_rate ? `${summary.disbursement_rate}%` : "—",
            icon: Percent, gradient: "from-sky-50 to-blue-50", border: "border-blue-200",
            iconBg: "bg-blue-100", iconColor: "text-blue-600", valueColor: "text-blue-900",
          },
        ];
        return (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="financial-hero-stats">
            {cards.map((c, i) => {
              const Icon = c.icon;
              return (
                <div key={i} className={`relative bg-gradient-to-br ${c.gradient} rounded-xl border ${c.border} p-5 shadow-sm hover:shadow-md transition-shadow overflow-hidden`}
                  data-testid={`financial-card-${i}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${c.iconBg}`}>
                      <Icon size={20} className={c.iconColor} strokeWidth={2} />
                    </div>
                  </div>
                  <div className={`text-2xl font-bold ${c.valueColor} leading-tight`}>
                    {loading ? <div className="h-7 w-28 bg-slate-200/60 rounded animate-pulse" /> : <AnimatedValue value={c.value} />}
                  </div>
                  <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mt-1.5">{c.title}</div>
                  <div className={`absolute -right-4 -bottom-4 w-24 h-24 rounded-full ${c.iconBg} opacity-20`} />
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Budget vs Actual - hidden per user request */}
      {/* <BudgetVsActualTable /> */}

      {/* Theme-wise Financial Performance - hidden per user request */}
      {false && themeMetrics.length > 0 && (
        <SectionHeader title="Theme-wise Financial Performance" subtitle="AWP · Disbursement by portfolio theme" collapsible defaultOpen={true}>
          {(() => {
            const maxDisb = Math.max(...themeMetrics.map(r => r.disbursed_cr || 0), 1);
            const maxAwp = Math.max(...themeMetrics.map(r => awpByTheme[r.theme] || 0), 1);
            // Prepare chart data: sorted by disbursement rate ascending
            const chartData = themeMetrics.map(r => {
              const awpVal = awpByTheme[r.theme] || 0;
              const disbRate = awpVal > 0 ? Math.round(r.disbursed_cr / awpVal * 1000) / 10 : 0;
              return { theme: r.theme, allocation_cr: r.allocation_cr || 0, disbursed_cr: r.disbursed_cr || 0, awp_cr: awpVal, disbRate };
            }).sort((a, b) => b.disbursed_cr - a.disbursed_cr);
            const maxAlloc = Math.max(...chartData.map(d => d.disbursed_cr), ...chartData.map(d => d.awp_cr), 1);
            return (
              <div className="relative" data-testid="theme-financial-heatmap">
                {/* Toggle - top right */}
                <div className="absolute -top-9 right-0 flex items-center gap-1 z-10">
                  <button onClick={() => setThemeFinView?.("chart")} data-testid="theme-fin-chart-btn"
                    className={`text-[10px] px-2.5 py-1 rounded-full border font-medium transition-colors ${(!themeFinView || themeFinView === "chart") ? "bg-[#006837] text-white border-[#006837]" : "bg-white text-slate-600 border-slate-200 hover:border-[#006837]"}`}>
                    Chart
                  </button>
                  <button onClick={() => setThemeFinView?.("table")} data-testid="theme-fin-table-btn"
                    className={`text-[10px] px-2.5 py-1 rounded-full border font-medium transition-colors ${themeFinView === "table" ? "bg-[#006837] text-white border-[#006837]" : "bg-white text-slate-600 border-slate-200 hover:border-[#006837]"}`}>
                    Table
                  </button>
                </div>

                {(!themeFinView || themeFinView === "chart") ? (
                  /* ─── Chart View ─── */
                  <div className="space-y-2.5" data-testid="theme-fin-chart">
                    {chartData.map((d, i) => {
                      const barPct = maxAlloc > 0 ? Math.min((d.disbursed_cr / maxAlloc) * 90, 90) : 0;
                      const awpPct = maxAlloc > 0 ? Math.min((d.awp_cr / maxAlloc) * 90, 90) : 0;
                      const themeColor = getThemeDotColor(d.theme);
                      return (
                        <div key={i} className="flex items-center gap-2 group relative" data-testid={`theme-chart-bar-${i}`}>
                          <div className="w-[18%] text-right pr-1 flex-shrink-0">
                            <span className="text-[11px] font-semibold text-slate-700 leading-tight">{displayTheme(d.theme)}</span>
                          </div>
                          <div className="flex-1 relative h-7">
                            {/* Disbursement bar */}
                            <div className="absolute inset-y-0 left-0 rounded-r-md transition-all duration-500 group-hover:opacity-100"
                              style={{ width: `${barPct}%`, backgroundColor: themeColor, opacity: 0.85 }} />
                            {/* AWP marker line */}
                            {d.awp_cr > 0 && (
                              <div className="absolute top-0 bottom-0 w-[2px] bg-slate-800 rounded"
                                style={{ left: `${awpPct}%` }} />
                            )}
                            {/* Disbursement rate label */}
                            <div className="absolute inset-y-0 flex items-center text-[11px] font-bold text-slate-700"
                              style={{ left: `${Math.max(barPct, awpPct) + 1.5}%` }}>
                              {d.disbRate > 0 ? `${d.disbRate}%` : "0%"}
                            </div>
                          </div>
                          {/* Hover tooltip */}
                          <div className="absolute left-[20%] top-full mt-1 hidden group-hover:block bg-slate-800 text-white text-[10px] rounded-md px-3 py-2 shadow-lg z-30 whitespace-nowrap pointer-events-none">
                            <span className="font-bold text-[11px] block mb-1">{displayTheme(d.theme)}</span>
                            <span className="block">Disbursement: {fmtCr(d.disbursed_cr)}</span>
                            <span className="block">AWP: {d.awp_cr > 0 ? fmtCr(d.awp_cr) : "—"}</span>
                            <span className="block">Disb %: {d.disbRate > 0 ? `${d.disbRate}%` : "0%"}</span>
                          </div>
                        </div>
                      );
                    })}
                    {/* X-axis */}
                    <div className="flex items-center gap-2 mt-1.5">
                      <div className="w-[18%]" />
                      <div className="flex-1 flex justify-between text-[9px] text-slate-400 border-t border-slate-100 pt-1" style={{ paddingRight: "10%" }}>
                        {[0, 0.25, 0.5, 0.75, 1].map(pct => (
                          <span key={pct}>{fmtCr(maxAlloc * pct)}</span>
                        ))}
                      </div>
                    </div>
                    {/* Legend */}
                    <div className="flex items-center gap-4 mt-2 pt-2 border-t border-slate-100 text-[10px] text-slate-600">
                      <div className="flex items-center gap-1.5">
                        <span className="w-6 h-3 rounded-sm inline-block" style={{ background: "linear-gradient(90deg, #D97706, #2563EB, #059669, #E11D48, #7C3AED)" }} />
                        <span>Disbursement (theme-colored bar)</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="w-[2px] h-4 bg-slate-800 rounded inline-block" />
                        <span>AWP Target</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="font-bold text-slate-700">%</span>
                        <span>Disbursement Rate (Disb / AWP)</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* ─── Table View (existing) ─── */
                  <div>
                <table className="w-full text-xs border-collapse table-fixed">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left text-slate-500 font-medium py-2 pr-4 w-[24%]">Theme</th>
                      <th className="text-center text-slate-600 font-semibold py-2 px-3 w-[24%]">AWP</th>
                      <th className="text-center text-slate-600 font-semibold py-2 px-3 w-[24%]">Disbursed</th>
                      <th className="text-center text-slate-600 font-semibold py-2 px-3 w-[16%]">
                        Disb %<InfoTooltip text="Disb % = (Disbursement ÷ AWP) × 100" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {themeMetrics.map((r, i) => {
                      const disbIntensity = r.disbursed_cr > 0 ? Math.max(0.08, r.disbursed_cr / maxDisb) : 0;
                      const awpVal = awpByTheme[r.theme] || 0;
                      const awpIntensity = awpVal > 0 ? Math.max(0.08, awpVal / maxAwp) : 0;
                      const disbRate = awpVal > 0 ? Math.round(r.disbursed_cr / awpVal * 1000) / 10 : null;
                      const rateClass = (rate) => rate >= 80 ? "bg-green-100 text-green-700" : rate >= 50 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-600";
                      return (
                        <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                          <td className="py-2 pr-4">
                            <ThemeChip theme={r.theme} />
                          </td>
                          <td className="py-2 px-3 text-center rounded-sm" data-testid="awp-theme-val"
                            style={{ backgroundColor: awpVal > 0 ? `rgba(180,83,9,${awpIntensity})` : "transparent",
                                     color: awpIntensity > 0.55 ? "#fff" : awpIntensity > 0 ? "#B45309" : "#CBD5E1" }}>
                            <span className="font-semibold">{awpVal > 0 ? fmtCrNoSymbol(awpVal) : "—"}</span>
                          </td>
                          <td className="py-2 px-3 text-center rounded-sm"
                            style={{ backgroundColor: r.disbursed_cr > 0 ? `rgba(0,104,55,${disbIntensity})` : "transparent",
                                     color: disbIntensity > 0.55 ? "#fff" : disbIntensity > 0 ? "#006837" : "#CBD5E1" }}>
                            <span className="font-semibold">{r.disbursed_cr ? fmtCrNoSymbol(r.disbursed_cr) : "—"}</span>
                          </td>
                          <td className="py-2 px-3 text-center">
                            {disbRate != null
                              ? <span className={`inline-flex items-center justify-center text-[11px] font-bold px-2 py-0.5 rounded-full ${rateClass(disbRate)}`}>{disbRate}%</span>
                              : <span className="text-slate-500">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    {(() => {
                      const totGrants = themeMetrics.reduce((s, r) => s + (r.grants || 0), 0);
                      const totAwp = themeMetrics.reduce((s, r) => s + (awpByTheme[r.theme] || 0), 0);
                      const totDisb = themeMetrics.reduce((s, r) => s + (r.disbursed_cr || 0), 0);
                      const totDisbRate = totAwp > 0 ? Math.round(totDisb / totAwp * 1000) / 10 : null;
                      return (
                        <tr className="border-t-2 border-slate-200 bg-slate-50">
                          <td className="py-2 pr-3 font-bold text-slate-800 text-[11px]">Total</td>
                          <td className="py-2 px-3 text-center font-bold text-slate-800">{totAwp > 0 ? fmtCrNoSymbol(totAwp) : "—"}</td>
                          <td className="py-2 px-3 text-center font-bold text-slate-800">{totDisb > 0 ? fmtCrNoSymbol(totDisb) : "—"}</td>
                          <td className="py-2 px-3 text-center font-bold text-slate-800">{totDisbRate != null ? `${totDisbRate}%` : "—"}</td>
                        </tr>
                      );
                    })()}
                  </tfoot>
                </table>
                <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 pt-2 border-t border-slate-100 text-[10px] text-slate-600">
                  {[{label:"AWP", rgb:"180,83,9"},{label:"Disbursed",rgb:"183,28,28"}].map(({label,rgb})=>(
                    <div key={label} className="flex items-center gap-1.5">
                      <span className="font-medium">{label}:</span>
                      {[0.1,0.35,0.65,1].map((o,i)=>(
                        <span key={o} className="flex items-center gap-0.5">
                          <span className="w-4 h-3 rounded-sm inline-block" style={{backgroundColor:`rgba(${rgb},${o})`}}/>
                          {["Low","Med","High","Max"][i]}
                        </span>
                      ))}
                    </div>
                  ))}
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium">Rates:</span>
                    <span className="px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">≥80% Good</span>
                    <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">50–79% OK</span>
                    <span className="px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 font-medium">&lt;50% Low</span>
                  </div>
                </div>
                  </div>
                )}
              </div>
            );
          })()}
        </SectionHeader>
      )}

      {/* Theme-wise Grant Size Distribution */}
      <ThemeSanctionMatrix />

      {/* Budget Distribution */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="budget-distribution-section">
        <div className="px-5 pt-4 pb-3 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Budget Distribution</h3>
            <p className="text-xs text-slate-500 mt-0.5">Budget across 4 categories by {budgetDistToggle === "theme" ? "portfolio theme" : "source of fund"}</p>
          </div>
          <div className="flex items-center gap-3">
            <select value={budgetDistToggle} onChange={e => setBudgetDistToggle(e.target.value)}
              className="text-[10px] px-2 py-1 rounded border border-slate-200 text-slate-700 font-medium bg-white cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#006837]"
              data-testid="budget-dist-group-select">
              <option value="theme">Thematic</option>
              <option value="trust">Source of Fund</option>
            </select>
            <div className="flex items-center gap-1">
              <button onClick={() => setBudgetDistView("chart")} data-testid="budget-dist-chart-btn"
                className={`text-[10px] px-2.5 py-1 rounded-full border font-medium transition-colors ${budgetDistView === "chart" ? "bg-[#006837] text-white border-[#006837]" : "bg-white text-slate-600 border-slate-200 hover:border-[#006837]"}`}>
                Chart
              </button>
              <button onClick={() => setBudgetDistView("table")} data-testid="budget-dist-table-btn"
                className={`text-[10px] px-2.5 py-1 rounded-full border font-medium transition-colors ${budgetDistView === "table" ? "bg-[#006837] text-white border-[#006837]" : "bg-white text-slate-600 border-slate-200 hover:border-[#006837]"}`}>
                Table
              </button>
            </div>
          </div>
        </div>
        <div className="p-4 overflow-x-auto">
          {budgetDist.data.length > 0 ? (() => {
            const rawCats = budgetDist.categories || [];
            const desiredOrder = ["Admin & Personnel", "Capital", "Programme", "Overhead"];
            const cats = desiredOrder.filter(c => rawCats.includes(c)).concat(rawCats.filter(c => !desiredOrder.includes(c)));
            const allVals = budgetDist.data.flatMap(row => cats.map(c => row[c] || 0)).filter(Boolean);
            const maxVal = Math.max(...allVals, 1);
            const colTotals = cats.map(c => budgetDist.data.reduce((s, row) => s + (row[c] || 0), 0));
            const grandTotal = colTotals.reduce((a, b) => a + b, 0);
            const STACK_COLORS = ["#006837", "#16A34A", "#F59E0B", "#64748B"];
            return budgetDistView === "chart" ? (
              /* ─── Stacked Bar Chart ─── */
              <div data-testid="budget-dist-chart">
                <div className="space-y-2">
                  {budgetDist.data.sort((a, b) => (b.Total || 0) - (a.Total || 0)).map((row, ri) => {
                    const total = row.Total || cats.reduce((s, c) => s + (row[c] || 0), 0);
                    const maxTotal = Math.max(...budgetDist.data.map(r => r.Total || cats.reduce((s, c) => s + (r[c] || 0), 0)), 1);
                    const rowPct = Math.min((total / maxTotal) * 90, 90);
                    return (
                        <div key={ri} className="flex items-center gap-2 group relative" data-testid={`budget-dist-bar-${ri}`}>
                        <div className="w-[18%] text-right pr-1 flex-shrink-0">
                          <span className="text-[11px] font-semibold text-slate-700 leading-tight truncate block">
                            {budgetDistToggle === "theme" ? displayTheme(row.label) : row.label}
                          </span>
                        </div>
                        <div className="flex-1 relative h-7">
                          {/* Stacked segments */}
                          <div className="absolute inset-y-0 left-0 flex rounded-r-md overflow-hidden" style={{ width: `${rowPct}%` }}>
                            {cats.map((c, ci) => {
                              const val = row[c] || 0;
                              if (val <= 0 || total <= 0) return null;
                              const segPct = (val / total) * 100;
                              return (
                                <div key={c} className="h-full transition-all duration-300"
                                  style={{ width: `${segPct}%`, backgroundColor: STACK_COLORS[ci % STACK_COLORS.length], opacity: 0.85 }} />
                              );
                            })}
                          </div>
                          {/* Total label */}
                          <div className="absolute inset-y-0 flex items-center text-[11px] font-bold text-slate-700"
                            style={{ left: `${rowPct + 1.5}%` }}>
                            {total > 0 ? fmtCrNoSymbol(total) : "—"}
                          </div>
                        </div>
                        {/* Hover tooltip - appears on row hover */}
                        <div className="absolute left-[20%] top-full mt-1 hidden group-hover:block bg-slate-800 text-white text-[10px] rounded-md px-3 py-2 shadow-lg z-30 whitespace-nowrap pointer-events-none">
                          <span className="font-bold text-[11px] block mb-1">{budgetDistToggle === "theme" ? displayTheme(row.label) : row.label}</span>
                          {cats.map((c, ci) => {
                            const val = row[c] || 0;
                            const pct = total > 0 ? Math.round(val / total * 100) : 0;
                            return val > 0 ? (
                              <span key={c} className="flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-sm inline-block" style={{ backgroundColor: STACK_COLORS[ci % STACK_COLORS.length] }} />
                                {c}: {fmtCrNoSymbol(val)} ({pct}%)
                              </span>
                            ) : null;
                          })}
                          <span className="border-t border-slate-600 mt-1 pt-1 font-bold block">Total: {fmtCrNoSymbol(total)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Legend */}
                <div className="flex flex-wrap items-center gap-3 mt-3 pt-2 border-t border-slate-100 text-[10px] text-slate-600">
                  {cats.map((c, ci) => (
                    <div key={c} className="flex items-center gap-1.5">
                      <span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: STACK_COLORS[ci % STACK_COLORS.length], opacity: 0.85 }} />
                      <span className="font-medium">{c}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              /* ─── Table View (existing) ─── */
              <table className="w-full text-xs border-collapse" data-testid="budget-dist-heatmap">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left text-slate-500 font-medium py-2 pr-4 min-w-[140px]">{budgetDistToggle === "theme" ? "Theme" : "Trust"}</th>
                    {cats.map(c => (
                      <th key={c} className="text-center text-slate-600 font-semibold py-2 px-3 min-w-[100px]">{c}</th>
                    ))}
                    <th className="text-center text-slate-800 font-bold py-2 px-3 min-w-[80px] bg-slate-50">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {budgetDist.data.map((row, ri) => (
                    <tr key={ri} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                      <td className="py-1.5 pr-3 font-medium text-slate-700 whitespace-nowrap text-[11px]">
                        {budgetDistToggle === "theme" ? <ThemeChip theme={row.label} /> : <span>{row.label}</span>}
                      </td>
                      {cats.map(c => {
                        const val = row[c] || 0;
                        const intensity = val > 0 ? Math.max(0.08, val / maxVal) : 0;
                        return (
                          <td key={c} className="py-1.5 px-3 text-center text-[11px] rounded-sm"
                            style={{
                              backgroundColor: val > 0 ? `rgba(0,104,55,${intensity})` : "transparent",
                              color: intensity > 0.55 ? "#fff" : intensity > 0 ? "#006837" : "#CBD5E1"
                            }}>
                            <span className="font-semibold">{val > 0 ? fmtCrNoSymbol(val) : "—"}</span>
                          </td>
                        );
                      })}
                      <td className="py-1.5 px-3 text-center text-[11px] font-bold text-slate-800 bg-slate-50">
                        {row.Total > 0 ? fmtCrNoSymbol(row.Total) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-200 bg-slate-50">
                    <td className="py-2 pr-3 font-bold text-slate-800 text-[11px]">Total</td>
                    {colTotals.map((t, i) => (
                      <td key={i} className="py-2 px-3 text-center text-[11px] font-bold text-slate-800">
                        {t > 0 ? <>{fmtCrNoSymbol(t)} <span className="text-slate-600 font-medium">({grandTotal > 0 ? Math.round(t / grandTotal * 100) : 0}%)</span></> : "—"}
                      </td>
                    ))}
                    <td className="py-2 px-3 text-center text-[11px] font-extrabold text-slate-900">
                      {grandTotal > 0 ? fmtCrNoSymbol(grandTotal) : "—"}
                    </td>
                  </tr>
                </tfoot>
              </table>
            );
          })() : <div className="py-8 text-center text-sm text-slate-600">Loading...</div>}
          {budgetDist.grants_without_budget > 0 && (
            <p className="text-[10px] text-slate-600 mt-2 italic" data-testid="budget-dist-footnote">
              Note: {budgetDist.grants_without_budget} grant{budgetDist.grants_without_budget > 1 ? "s" : ""} do not have a budget breakdown in the source system and are excluded from this table.
              The Total Sanctioned KPI above reflects all grants.
            </p>
          )}
        </div>
      </div>

      {/* Grant Financial Detail Table */}
      <SectionHeader title="Grant Financial Details" subtitle={`${grantsTable.total} grants`} collapsible defaultOpen={true}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="grants-financial-table">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left text-xs font-medium text-slate-500 uppercase py-2 pr-4">Grant ID</th>
                <th className="text-left text-xs font-medium text-slate-500 uppercase py-2">Grantee</th>
                <th className="text-left text-xs font-medium text-slate-500 uppercase py-2">Theme</th>
                <th className="text-right text-xs font-medium text-slate-500 uppercase py-2">Sanctioned</th>
                <th className="text-right text-xs font-medium text-slate-500 uppercase py-2">Disbursed</th>
                <th className="text-right text-xs font-medium text-slate-500 uppercase py-2 whitespace-nowrap">
                  Disb %<InfoTooltip text="Disbursement Rate = Disbursed ÷ Sanctioned × 100" />
                </th>
                <th className="text-right text-xs font-medium text-slate-500 uppercase py-2">Payments</th>
              </tr>
            </thead>
            <tbody>
              {grantsTable.data.map((r, i) => (
                <tr key={i} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                  <td className="py-2.5 font-mono text-xs pr-4">{r.grant_id}</td>
                  <td className="py-2.5 text-xs max-w-xs">
                    <div className="truncate font-medium text-slate-900">{r.grantee}</div>
                    <div className="truncate text-slate-600 text-xs">{r.title}</div>
                  </td>
                  <td className="py-2.5"><ThemeChip theme={r.theme} /></td>
                  <td className="py-2.5 text-right text-xs font-medium">{fmtCr(r.allocation_cr)}</td>
                  <td className="py-2.5 text-right text-xs font-semibold text-[#006837]">{fmtCr(r.disbursed_cr)}</td>
                  <td className="py-2.5 text-right text-xs">
                    {r.disbursement_rate != null ? (
                      <span className={`px-1.5 py-0.5 rounded text-xs ${r.disbursement_rate >= 80 ? "bg-green-100 text-green-700" : r.disbursement_rate >= 40 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                        {r.disbursement_rate}%
                      </span>
                    ) : "–"}
                  </td>
                  <td className="py-2.5 text-right text-xs text-slate-500">{r.payments}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {grantsTable.total > 20 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
              <span className="text-xs text-slate-500">
                Showing {(tablePage - 1) * 20 + 1}–{Math.min(tablePage * 20, grantsTable.total)} of {grantsTable.total}
              </span>
              <div className="flex gap-2">
                <button onClick={() => setTablePage(p => Math.max(1, p - 1))} disabled={tablePage === 1}
                  className="text-xs px-3 py-1.5 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40">Prev</button>
                <button onClick={() => setTablePage(p => p + 1)} disabled={tablePage * 20 >= grantsTable.total}
                  className="text-xs px-3 py-1.5 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40">Next</button>
              </div>
            </div>
          )}
        </div>
      </SectionHeader>
    </div>
  );
}
